<template>
	<AppDialog ref="dialog_container" dialogName="EmailVerificationDialog" width="500px">
		<div class="verify-email-dialog display-flex flex-column">
			<span class="dialog-title flex-centerize">Verify {{ typeToVerifyText }} email address</span>
			<div class="description-text mt-4 text-[14px] mb-4">
				We just sent you an email with a confirmation code to
				<span class="email-text">{{ emailToVerify }}</span
				>.<br />
				Enter it below to verify this email address
			</div>
			<div class="otp-input-container display-flex flex-row">
				<v-otp-input :error="isWrongCode" :disabled="isLoading" @finish="isCodeAuthenticated" type="number" length="6"></v-otp-input>
			</div>
			<div v-if="isWrongCode" class="otp-error-message">Wrong Code</div>
			<div v-if="isLoading" class="otp-loader-container display-flex flex-column">
				<Loader v-show="isLoading" />
			</div>
			<div class="bottom-section mt-4">
				<div v-if="!isEmailResent">
					Didn’t get an email?
					<span @click="sendAnotherCode" class="primary-link-style ml-1">Resend code</span>
				</div>
				<div v-else>
					<div>We've just sent you another email with a confirmation code</div>
					<span @click="sendAnotherCode" class="primary-link-style ml-1">Resend code</span>
				</div>
			</div>
		</div>
	</AppDialog>
</template>

<script>
import { DataTypeEvaluator } from '@ply-io/data-types';

import AppDialog from '@/components/dialogs/AppDialog/AppDialog';
import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import { sendVerifyEmail, verifyReplyToEmailOtp, verifyUnsubscribeContactEmailOtp } from '@/store/modules/accountEmailDomains/actions';
import { mapActionsShorthand } from '@/store/utils/mappers';
import { VerificationEmailTypes } from '@/utils/transactional-emails.const';
const { isEmail } = new DataTypeEvaluator();

export default {
	name: 'EmailVerificationDialog',
	components: { AppDialog },
	props: {
		typeToVerify: { type: String },
		emailToVerify: { type: String },
		dialogPromiseHandlers: { type: Object },
	},
	methods: {
		...mapActionsShorthand({ sendVerifyEmail, verifyReplyToEmailOtp, verifyUnsubscribeContactEmailOtp }),
		async sendVerifyEmail() {
			const isEmailValid = isEmail(this.emailToVerify);
			if (!isEmailValid) {
				this.$toast.error('Invalid email address');
				return;
			}
			this.isLoading = true;
			try {
				await this.sendVerifyEmail(this.emailToVerify);
			} catch (error) {
				this.$toast.error('Failed to send verification email to: ' + this.emailToVerify);
			} finally {
				this.isLoading = false;
			}
		},
		async isCodeAuthenticated(otp) {
			try {
				this.isLoading = true;
				const { is_valid: isAuthenticated } = await this.verificationActionToUse(otp);
				if (!isAuthenticated) {
					this.isWrongCode = true;
					return;
				}
				this.isWrongCode = false;
				this.$toast.success(this.successMessage, { timeout: 5000 });
				this.close();
			} catch (error) {
				this.$toast.error('Failed to verify code');
			} finally {
				this.isLoading = false;
			}
		},
		async sendAnotherCode() {
			await this.sendVerifyEmail();
			this.isEmailResent = true;
		},
		close() {
			AppDialogManagerService.closeCurrentDialog();
		},
	},
	computed: {
		verificationActionToUse() {
			return this.typeToVerify === VerificationEmailTypes.REPLY_TO ? this.verifyReplyToEmailOtp : this.verifyUnsubscribeContactEmailOtp;
		},
		successMessage() {
			if (this.typeToVerify === VerificationEmailTypes.REPLY_TO) {
				return 'Email verified successfully, you can now use this \nemail as a reply-to email in the "Send an email" action.';
			}
			return 'Email verified successfully,\nthis email will now receive unsubscribe requests.';
		},
		typeToVerifyText() {
			return this.typeToVerify === VerificationEmailTypes.REPLY_TO ? 'reply-to' : 'unsubscribe contact';
		},
	},
	data() {
		return {
			isLoading: false,
			isWrongCode: false,
			isEmailResent: false,
		};
	},
};
</script>

<style scoped lang="scss">
.verify-email-dialog {
	border-radius: 15px !important;
	box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.16) !important;
	padding: 20px 30px 14px 30px;

	::v-deep(.widget-preview) {
		min-height: 350px !important;
	}
}
.bottom-section {
	font-size: 14px;
}

.description-text {
	font-weight: 300;
	color: var(--primary-font-color);
}
.verify-email-dialog {
	.otp-error-message {
		font-size: 12px;
		color: #de2928;
		text-align: left;
	}

	.otp-text {
		font-size: 18px;
		text-align: center;
		padding-bottom: 10px;
		color: var(--primary-font-color);
	}

	.otp-loader-container {
		height: 80px;
	}

	.otp-input-container {
		::v-deep {
			.v-text-field__slot {
				height: 91px;
				padding: 0px;
				max-width: 70px;
				box-shadow: 0px 3px 10px #00000029;
			}
			.v-input__slot {
				min-height: 91px !important;
			}
		}
	}

	.email-text {
		color: var(--primary-color);
	}
}
</style>
